import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiService from '../services/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { ArrowLeft, UserPlus, Check, Loader2 } from 'lucide-react';

const RegisterAdmin = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [loadingShop, setLoadingShop] = useState(true);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [apiError, setApiError] = useState('');
  const [formData, setFormData] = useState({
    fullname: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchShop = async () => {
      try {
        const response = await apiService.getShopDetails(id);
        if (response.success) {
          setShop(response.data.shop);
        }
      } catch (err) {
        // Shop not found
      } finally {
        setLoadingShop(false);
      }
    };
    fetchShop();
  }, [id]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (apiError) {
      setApiError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullname.trim()) newErrors.fullname = 'Admin name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.phoneNumber.trim()) newErrors.phoneNumber = 'Phone number is required';
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setApiError('');

    try {
      const response = await apiService.createShopUser({
        shopMongoId: id,
        fullname: formData.fullname,
        email: formData.email.toLowerCase(),
        phoneNumber: formData.phoneNumber,
        role: 'shopAdmin',
        password: formData.password,
        confirmPassword: formData.confirmPassword,
      });

      if (response.success) {
        setIsSuccess(true);
        setTimeout(() => {
          navigate(`/shops/${id}`);
        }, 2000);
      } else {
        setApiError(response.message || 'Failed to register admin');
      }
    } catch (error) {
      setApiError(error.message || 'Failed to register admin');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingShop) {
    return (
      <div className="p-6 md:p-8 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-[#2563EB] animate-spin mx-auto" />
          <p className="text-[#94A3B8] mt-2">Loading...</p>
        </div>
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="p-6 md:p-8" data-testid="shop-not-found">
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-[#E2E8F0] mb-2">Shop not found</h2>
          <p className="text-[#94A3B8] mb-4">The shop you're looking for doesn't exist.</p>
          <Button 
            onClick={() => navigate('/shops')}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
          >
            Back to Shops
          </Button>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="p-6 md:p-8" data-testid="register-admin-success">
        <div className="max-w-2xl mx-auto">
          <div className="bg-[#1E293B] border border-[#334155] rounded-lg p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-[#22C55E]/20 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-[#4ADE80]" />
            </div>
            <h2 className="text-2xl font-bold text-[#E2E8F0] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Admin Registered Successfully!
            </h2>
            <p className="text-[#94A3B8]">
              Redirecting to shop details...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="register-admin-page">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate(`/shops/${id}`)}
          className="text-[#94A3B8] hover:text-[#E2E8F0] hover:bg-[#334155]/50 mb-4 -ml-4"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to {shop.shopName}
        </Button>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#0EA5E9]/20 flex items-center justify-center">
            <UserPlus className="w-6 h-6 text-[#38BDF8]" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[#E2E8F0] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Register Admin
            </h1>
            <p className="text-[#94A3B8]">Add a new admin for {shop.shopName}</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-2xl">
        <form onSubmit={handleSubmit} className="bg-[#1E293B] border border-[#334155] rounded-lg p-6 md:p-8 space-y-6">
          {/* API Error */}
          {apiError && (
            <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-md p-3">
              <p className="text-[#EF4444] text-sm">{apiError}</p>
            </div>
          )}

          {/* Admin Name */}
          <div className="space-y-2">
            <Label htmlFor="fullname" className="text-[#E2E8F0] text-sm font-medium">
              Full Name <span className="text-[#EF4444]">*</span>
            </Label>
            <Input
              id="fullname"
              value={formData.fullname}
              onChange={(e) => handleChange('fullname', e.target.value)}
              placeholder="Enter admin's full name"
              className={`bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B] focus:ring-2 focus:ring-[#2563EB] focus:border-transparent h-11 ${errors.fullname ? 'border-[#EF4444]' : ''}`}
              data-testid="admin-name-input"
            />
            {errors.fullname && <p className="text-[#EF4444] text-xs">{errors.fullname}</p>}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-[#E2E8F0] text-sm font-medium">
              Email Address <span className="text-[#EF4444]">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="admin@example.com"
              className={`bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B] focus:ring-2 focus:ring-[#2563EB] focus:border-transparent h-11 ${errors.email ? 'border-[#EF4444]' : ''}`}
              data-testid="admin-email-input"
            />
            {errors.email && <p className="text-[#EF4444] text-xs">{errors.email}</p>}
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phoneNumber" className="text-[#E2E8F0] text-sm font-medium">
              Phone Number <span className="text-[#EF4444]">*</span>
            </Label>
            <Input
              id="phoneNumber"
              value={formData.phoneNumber}
              onChange={(e) => handleChange('phoneNumber', e.target.value)}
              placeholder="310-555-0102"
              className={`bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B] focus:ring-2 focus:ring-[#2563EB] focus:border-transparent h-11 ${errors.phoneNumber ? 'border-[#EF4444]' : ''}`}
              data-testid="admin-phone-input"
            />
            {errors.phoneNumber && <p className="text-[#EF4444] text-xs">{errors.phoneNumber}</p>}
          </div>

          {/* Password */}
          <div className="space-y-2">
            <Label htmlFor="password" className="text-[#E2E8F0] text-sm font-medium">
              Password <span className="text-[#EF4444]">*</span>
            </Label>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => handleChange('password', e.target.value)}
              placeholder="Minimum 8 characters"
              className={`bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B] focus:ring-2 focus:ring-[#2563EB] focus:border-transparent h-11 ${errors.password ? 'border-[#EF4444]' : ''}`}
              data-testid="admin-password-input"
            />
            {errors.password && <p className="text-[#EF4444] text-xs">{errors.password}</p>}
          </div>

          {/* Confirm Password */}
          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-[#E2E8F0] text-sm font-medium">
              Confirm Password <span className="text-[#EF4444]">*</span>
            </Label>
            <Input
              id="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => handleChange('confirmPassword', e.target.value)}
              placeholder="Re-enter password"
              className={`bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B] focus:ring-2 focus:ring-[#2563EB] focus:border-transparent h-11 ${errors.confirmPassword ? 'border-[#EF4444]' : ''}`}
              data-testid="admin-confirm-password-input"
            />
            {errors.confirmPassword && <p className="text-[#EF4444] text-xs">{errors.confirmPassword}</p>}
          </div>

          {/* Submit Button */}
          <div className="flex gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/shops/${id}`)}
              className="border-[#334155] text-[#94A3B8] hover:bg-[#334155] hover:text-[#E2E8F0]"
              data-testid="cancel-button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white flex-1 sm:flex-none"
              data-testid="submit-button"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Registering...
                </>
              ) : (
                'Register Admin'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterAdmin;
